import { useTranslation } from 'react-i18next'
import { connect, useDataItem } from 'scrivito'

import { ActivityTab } from '../SharedTabs/ActivityTab/ActivityTab'
import { ContactTab } from '../SharedTabs/ContactTab/ContactTab'
import { OpportunityTab } from '../SharedTabs/OpportunityTab/OpportunityTab'
import { OrderTab } from '../SharedTabs/OrderTab/OrderTab'
import { QuoteTab } from '../SharedTabs/QuoteTab/QuoteTab'
import { NavTabs, NavTabItem } from '@/Atomic/Templates/NavTabs'
import { NavTab } from '@/Atomic/Widgets/DataItemTabs/const'
import { ActivityDocumentRelation } from '@/Data/ActivityDocumentRelation/ActivityDocumentRelationDataClass'
import { DataClassName } from '@/Data/const'
import { ContactDocumentRelation } from '@/Data/ContactDocumentRelation/ContactDocumentRelationDataClass'
import { DocumentProjectRelation } from '@/Data/DocumentProjectRelation/DocumentProjectRelationDataClass'
import { useFeatureFlagAccess } from '@/utils/featureFlagConfig'

import '../DataItemNavTabs.scss'

const ALL_NAV_TABS: NavTab[] = [
  NavTab.ACTIVITY,
  NavTab.CONTACT,
  NavTab.COMPANY,
  NavTab.OPPORTUNITY,
  NavTab.QUOTE,
  NavTab.ORDER,
]

export const DocumentDataItemTabs: React.FC = connect(() => {
  const { t } = useTranslation()
  const { filterTabs } = useFeatureFlagAccess()
  const dataItem = useDataItem()
  const dataItemId = dataItem?.id() ?? ''

  if (!dataItemId) return

  const visibleNavTabs = filterTabs(ALL_NAV_TABS)
  const isVisible = (tab: NavTab) => visibleNavTabs.includes(tab)

  const activityCount = isVisible(NavTab.ACTIVITY)
    ? (ActivityDocumentRelation.all()
        .transform({ filters: { document: dataItemId } })
        .count() ?? 0)
    : 0

  const contactCount = isVisible(NavTab.CONTACT)
    ? (ContactDocumentRelation.all()
        .transform({
          filters: {
            document: dataItemId,
            contactEntity: DataClassName.PERSON,
          },
        })
        .count() ?? 0)
    : 0

  const companyCount = isVisible(NavTab.COMPANY)
    ? (ContactDocumentRelation.all()
        .transform({
          filters: {
            document: dataItemId,
            contactEntity: DataClassName.COMPANY,
          },
        })
        .count() ?? 0)
    : 0

  const opportunityCount = isVisible(NavTab.OPPORTUNITY)
    ? (DocumentProjectRelation.all()
        .transform({
          filters: {
            document: dataItemId,
            projectEntity: DataClassName.OPPORTUNITY,
          },
        })
        .count() ?? 0)
    : 0

  const quoteCount = isVisible(NavTab.QUOTE)
    ? (DocumentProjectRelation.all()
        .transform({
          filters: {
            document: dataItemId,
            projectEntity: DataClassName.QUOTE,
          },
        })
        .count() ?? 0)
    : 0

  const orderCount = isVisible(NavTab.ORDER)
    ? (DocumentProjectRelation.all()
        .transform({
          filters: {
            document: dataItemId,
            projectEntity: DataClassName.ORDER,
          },
        })
        .count() ?? 0)
    : 0

  const tabsByNavTab: Array<{ navTab: NavTab; tab: NavTabItem }> = [
    {
      navTab: NavTab.ACTIVITY,
      tab: {
        id: 'activities',
        title: t('pages.activities.title'),
        icon: 'activity',
        count: activityCount,
        content: (
          <ActivityTab
            searchInputName="search document-activity"
            activityRelation="document"
          />
        ),
      },
    },
    {
      navTab: NavTab.CONTACT,
      tab: {
        id: 'contacts',
        title: t('components.contact.title'),
        icon: 'user-multiple',
        count: contactCount,
        content: (
          <ContactTab
            searchInputName="search document-contact"
            contactRelation="document"
            contactEntity="Person"
          />
        ),
      },
    },
    {
      navTab: NavTab.COMPANY,
      tab: {
        id: 'companies',
        title: t('components.company.title'),
        icon: 'building',
        count: companyCount,
        content: (
          <ContactTab
            searchInputName="search document-company"
            contactRelation="document"
            contactEntity="Company"
          />
        ),
      },
    },
    {
      navTab: NavTab.OPPORTUNITY,
      tab: {
        id: 'opportunities',
        title: t('components.breadcrumbs.opportunities'),
        icon: 'fire',
        count: opportunityCount,
        content: (
          <OpportunityTab
            searchInputName="search document-opportunity"
            subjectRelation="document"
          />
        ),
      },
    },
    {
      navTab: NavTab.QUOTE,
      tab: {
        id: 'quotes',
        title: t('components.breadcrumbs.quotes'),
        icon: 'invoice',
        count: quoteCount,
        content: (
          <QuoteTab
            searchInputName="search document-quote"
            subjectRelation="document"
          />
        ),
      },
    },
    {
      navTab: NavTab.ORDER,
      tab: {
        id: 'orders',
        title: t('components.breadcrumbs.orders'),
        icon: 'file-check',
        count: orderCount,
        content: (
          <OrderTab
            searchInputName="search document-order"
            subjectRelation="document"
          />
        ),
      },
    },
  ]

  const tabs: NavTabItem[] = tabsByNavTab
    .filter((entry) => isVisible(entry.navTab))
    .map((entry) => entry.tab)

  if (!tabs.length) return

  return <NavTabs tabs={tabs} />
})
